import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate L6-10 del Barbaro (manuale V1, rr. 2144-2233).
 *
 * **14 mosse** disponibili per il level-up dei livelli 6-10 (oltre alle 16
 * mosse L2-5 ancora disponibili).
 *
 * **Pattern critici**:
 *
 * - ***Potenza Indomabile*** (replaces *Assalto Irruento*): **7° applicazione
 *   strutturale di `MoveSchema.replaces`** (precedenti: 2 Mago, 2 Monaco,
 *   1 Ranger, 3 Paladino). Stesso trigger (*tagli e spacchi* con 10+ e nemico
 *   risponde), ma +1d8 anziché +1d4.
 *
 * - ***Essere il Totem*** (FI-30 stub description-only): pattern UNICO —
 *   concede una **mossa specifica predeterminata** di un'altra classe
 *   non-multiclasse, in base alla specie scelta in *Animale Totemico*
 *   (Istinto Misticismo, mossa-config con `MoveSchema.choices` su 4 specie):
 *   - aquila → `ladro.io-sparo-per-primo`
 *   - corvo → `guerriero.vedere-rosso`
 *   - lupo → `paladino.attacco-a-seguire`
 *   - orso → `paladino.difensore-devoto`
 *
 *   Il manuale specifica esplicitamente «Questa mossa *non si considera* una
 *   mossa multiclasse». Pattern **diverso** da FI-17 (multiclasse "a scelta
 *   tra classi"): qui la concessione è predeterminata da una scelta interna
 *   alla classe-source. Aperto come FI-30 (P3) nuovo nella PR Barbaro;
 *   strutturazione attesa con la logica builder cross-class concessioni
 *   dirette.
 *
 *   `prerequisites: ['barbaro.istinto-misticismo']` strutturato (l'Istinto
 *   *Misticismo: Animale Totemico* è la mossa-config che porta la `choices`
 *   sulla specie).
 *
 * - ***Il Khan dei Khan*** (FI-64 risolto Sprint C): evolve la *Banda di
 *   Guerra* a **gregario di livello 3** (8 elementi, 8 PF, +3 a una
 *   Caratteristica). Upgrade strutturale applicato a runtime via
 *   `FollowerTemplate.upgrades` sulla Banda di Guerra. `prerequisites:
 *   ['barbaro.banda-di-guerra']` strutturato.
 *
 * - ***Berserker*** / ***Ira Persistente*** (req: *Ira*): catena di
 *   prerequisiti strutturata L2-5 → L6-10. Pattern noto (3 catene del Ranger,
 *   3 dello Sciamano).
 *
 * - ***Ammazzali Tutti*** (multiclasse): tag `'multiclasse'`, description-only
 *   — Guerriero/Bardo/Ladro/Ranger/Sciamano. Pattern FI-17, coerente con
 *   *Fame per La Distruzione* (L2-5).
 *
 * - ***Passo del Lampo*** description-only riferimento a *Sempre in Movimento*
 *   (L2-5, +1 al tiro). Pattern di cross-reference textuale (no strutturazione
 *   necessaria — Passo del Lampo è prendibile senza il prereq).
 *
 * **5 mosse con prerequisites strutturati**:
 * - *Il Khan dei Khan* req *Banda di Guerra*
 * - *Un Segno di Forza* req *Appetiti Erculei*
 * - *Berserker* req *Ira*
 * - *Ira Persistente* req *Ira*
 * - *Essere il Totem* req *Misticismo: Animale Totemico*
 *
 * **1 mossa con `replaces` strutturato**:
 * - *Potenza Indomabile* sostituisce *Assalto Irruento*
 */

const urloDiBattaglia: Move = {
  id: 'barbaro.urlo-di-battaglia',
  name: 'Urlo di Battaglia',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger:
    "Quando entri in battaglia dando una dimostrazione di forza (un urlo, grida d'incitazione, una danza di guerra)",
  description:
    'Tira+**COS**. Con 10+, scegli **entrambe le opzioni**. Con 7–9, scegline una:\n\n- i tuoi alleati sono incoraggiati e prendono **+1 al prossimo tiro**;\n- i tuoi nemici si spaventano, e agiscono di conseguenza (evitandoti, nascondendosi, attaccandoti in preda al panico, ecc.), a discrezione del GM.',
  roll: {
    stat: 'costituzione',
    on10plus: { text: 'Entrambe le opzioni.' },
    on7to9: { text: 'Scegli 1 opzione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilKhanDeiKhan: Move = {
  id: 'barbaro.il-khan-dei-khan',
  name: 'Il Khan dei Khan',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Sempre (effetto continuo sulla Banda di Guerra)',
  description:
    'Ora la tua *Banda di Guerra* è formata da **otto elementi** (e il GM ne terrà conto!). Consideralo un **gregario di livello 3**, i PF massimi diventano 8, e ha un singolo modificatore di Caratteristica a **+3** anziché a +2.',
  tags: [],
  prerequisites: ['barbaro.banda-di-guerra'],
  replaces: [],
  // **FI-64 (Sprint C)**: upgrade strutturale applicato a runtime via
  // `FollowerTemplate.upgrades` in `src/data/followers/barbaro-banda-di-guerra.ts`
  // (campi `levelOverride: 3`, `hpRuleOverride: { type: 'fixed', value: 8 }`,
  // `modifierBonusOverride: { fromChoiceGroup: 'modificatore', amount: 3 }`).
  // Quando il PG ha entrambe le mosse, `applyUpgrades` (D8b in
  // `followerUpgrades.ts`) sostituisce i campi base del template prima di
  // chiamare `buildFollowerFromTemplate`. Niente più stub description-only.
};

const unSegnoDiForza: Move = {
  id: 'barbaro.un-segno-di-forza',
  name: 'Un Segno di Forza',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger:
    'Quando soddisfi un appetito fino al suo estremo (distruggendo qualcosa di inestimabile, ottenendo enorme fama, ricchezze, potere, ecc.)',
  description:
    "Puoi scegliere di risolverlo. Se lo fai, **cancellalo dalla lista, cura tutte le tue ferite, emorragie, fratture, malattie, veleni e Debilitazioni, e segna un PE**. Puoi sempre continuare a perseguire quell'appetito, ma non ne senti più il bisogno impellente che avevi un tempo. Sostituiscilo con un altro appetito dalla lista o con uno inventato.\n\nQuando **usi per la prima volta questa mossa**, puoi marchiarti con un simbolo della tua forza (una lunga treccia legata con campanelli, cicatrici rituali, tatuaggi, ecc.). Qualunque creatura senziente veda quel simbolo capisce istintivamente che sei un avversario temibile e ti tratterà di conseguenza.",
  tags: [],
  prerequisites: ['barbaro.appetiti-erculei'],
  replaces: [],
  // FI-18: setup-levelup canone. Descrizione del simbolo permanente di forza.
  narrativeFields: [
    {
      id: 'descrizione-simbolo',
      label: 'Descrivi il tuo simbolo di forza',
      placeholder:
        'es. Una lunga treccia legata con campanelli, cicatrici rituali, tatuaggi, un mantello fatto della pelle di un nemico…',
      required: true,
      multiline: true,
    },
  ],
};

const ammazzaliTutti: Move = {
  id: 'barbaro.ammazzali-tutti',
  name: 'Ammazzali Tutti',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando prendi questa mossa',
  description:
    'Prendi una mossa non multiclasse tra quelle del Guerriero, del Bardo, del Ladro, del Ranger o dello Sciamano.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool ristretto a 5 classi (simmetrico a *Fame per La Distruzione*).
  multiclass: {
    picks: {
      kind: 'fixed-classes',
      classes: ['guerriero', 'bardo', 'ladro', 'ranger', 'sciamano'],
    },
  },
};

const berserker: Move = {
  id: 'barbaro.berserker',
  name: 'Berserker',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando entri in *Ira*',
  description:
    "Con 12+, ottieni **4 prese**. Inoltre, aggiungi l'opzione «*Dimezzare i danni appena subiti (dopo aver sottratto l'Armatura)*» a quelle che puoi scegliere quando spendi prese di *Ira*.",
  tags: [],
  prerequisites: ['barbaro.ira'],
  replaces: [],
};

const iraPersistente: Move = {
  id: 'barbaro.ira-persistente',
  name: 'Ira Persistente',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando entri in *Ira*',
  description:
    'Se ottieni un risultato di 7+, puoi decidere di prendere **1d4 prese aggiuntive**. Se lo fai, subisci una penalità di **-1 continuato** (in aggiunta a quella eventualmente derivante da un 7-9) a tutti i tiri per entrare in *Ira* finché non riesci a goderti una notte di sonno ininterrotto.',
  tags: [],
  prerequisites: ['barbaro.ira'],
  replaces: [],
};

const potenzaIndomabile: Move = {
  id: 'barbaro.potenza-indomabile',
  name: 'Potenza Indomabile',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando *tagli e spacchi*',
  description:
    "Con 10+, se decidi di lasciare comunque che l'avversario risponda, oltre agli effetti aggiuntivi già previsti (in genere +1d6 danni), infliggi **+1d8 danni**.\n\nQuesta mossa sostituisce *Assalto Irruento* (L2-5, +1d4): stesso trigger, +1d8 anziché +1d4.",
  tags: [],
  prerequisites: [],
  // Sostituisce `barbaro.assalto-irruento` (L2-5, +1d4) via `MoveSchema.replaces`.
  replaces: ['barbaro.assalto-irruento'],
};

const spacca: Move = {
  id: 'barbaro.spacca',
  name: 'Spacca!',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando *tagli e spacchi*',
  description:
    "Con un 12+, infliggi il tuo Danno e scegli qualcosa di fisico che il tuo nemico ha (un'arma, la sua posizione, una capacità di movimento o attacco, ecc.): **lo perde**, almeno per un po'.\n\nQuel che hai spaccato resta rotto finché ha senso nella *fiction*. Di solito una nuova spada non compare per magia: l'avversario dovrà riuscire a recuperarla! E se lo bracchi senza tregua difficilmente potrà curare la gamba rotta o recuperare una posizione di vantaggio.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const unBuonGiornoPerMorire: Move = {
  id: 'barbaro.un-buon-giorno-per-morire',
  name: 'Un Buon Giorno per Morire',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Finché hai un numero di PF rimanenti pari o inferiori a 4',
  description:
    'Prendi **+1 continuato** a tutti i tiri per usare le mosse (ma non ai danni o ad altro!).',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const quelloCheBussa: Move = {
  id: 'barbaro.quello-che-bussa',
  name: 'Quello che Bussa',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando *sfidi il pericolo*',
  description:
    'Con 12+, puoi **distruggere la minaccia stessa** o **infliggere il tuo dado di Danno** a chi ha causato il pericolo (a discrezione del GM).',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const passoDelLampo: Move = {
  id: 'barbaro.passo-del-lampo',
  name: 'Passo del Lampo',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger:
    'Quando compi uno scatto a velocità impossibile per raggiungere un posto nelle tue immediate vicinanze',
  description:
    'Tira+**DES**. Con 10+, scegli **3**. Con 7-9, scegli **2**:\n\n- finisci esattamente dove volevi (e ignori le comuni leggi della fisica, se serve);\n- nessuno è in grado di vederti muovere o di capire in che direzione sei andato;\n- prendi qualcuno o qualcosa nelle vicinanze e lo porti via con te;\n- ottieni un vantaggio tattico: **+1 al prossimo tiro**.\n\nSe hai la mossa *Sempre in Movimento* (L2-5), ottieni **+1 al tiro** per *Passo del Lampo*.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Scegli 3 opzioni dalla lista.' },
    on7to9: { text: 'Scegli 2 opzioni dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const einherjar: Move = {
  id: 'barbaro.einherjar',
  name: 'Einherjar',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando *fai baldoria*',
  description:
    'Oltre al bonus per le monete aggiuntive, **somma COS al tiro** (massimo +3 complessivo). Con 12+, inoltre, puoi scegliere quante opzioni desideri **e la festa che avete dato diventerà leggendaria: tutti ne parleranno per molto tempo a venire.**',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const essereIlTotem: Move = {
  id: 'barbaro.essere-il-totem',
  name: 'Essere il Totem',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger: 'Quando prendi questa mossa',
  description:
    'Scegli un aspetto fisico, metafisico o caratteriale che ti faccia somigliare al tuo *Animale Totemico*. Gli esemplari del medesimo tipo del tuo *Animale Totemico* ti riconoscono come un membro della loro stessa razza quando li incontri e ottieni un **+1 continuato a ogni tiro per usare una mossa contro di essi**.\n\nInoltre, in base al tipo di *Animale Totemico* che hai scelto in precedenza, acquisisci la seguente mossa:\n\n- **aquila**: *Io Sparo per Primo* del Ladro;\n- **corvo**: *Vedere Rosso* del Guerriero;\n- **lupo**: *Attacco a Seguire* del Paladino;\n- **orso**: *Difensore Devoto* del Paladino.\n\nQuesta mossa **non si considera** una mossa multiclasse.',
  tags: [],
  prerequisites: ['barbaro.istinto-misticismo'],
  replaces: [],
  // FI-18: setup-levelup canone. Descrizione dell'aspetto similtotemico permanente.
  narrativeFields: [
    {
      id: 'descrizione-aspetto-totem',
      label: 'Descrivi il tuo aspetto similtotemico',
      placeholder:
        "es. Occhi gialli di lupo, pelle che richiama le piume del corvo, indole protettiva di un orso, posa fiera dell'aquila…",
      required: true,
      multiline: true,
    },
  ],
  // **FI-30 (D6)**: la mossa concede una mossa di un'altra classe in base
  // alla specie totemica scelta in `barbaro.istinto-misticismo`. Pattern
  // UNICO nel manuale V1 («Questa mossa non si considera una mossa
  // multiclasse»). Distinto da FI-17 (multiclasse a pool aperto).
  grantsMoveChoice: {
    kind: 'from-choice-mapping',
    sourceMoveId: 'barbaro.istinto-misticismo',
    mapping: {
      aquila: 'ladro.io-sparo-per-primo',
      corvo: 'guerriero.vedere-rosso',
      lupo: 'paladino.attacco-a-seguire',
      orso: 'paladino.difensore-devoto',
    },
  },
};

const perIlDioDelSangue: Move = {
  id: 'barbaro.per-il-dio-del-sangue',
  name: 'Per il Dio del Sangue',
  type: 'advanced_6_10',
  classId: 'barbaro',
  trigger:
    'Quando compi un rito sacrificale in cui offri ai tuoi dèi (o gli spiriti dei tuoi antenati, il tuo totem, ecc.) qualcosa che ritengono importante (oro, sangue, ossa o simili)',
  description:
    "Sei stato iniziato secondo l'antica via, la via del sacrificio. Scegli qualcosa che i tuoi dèi (o gli spiriti dei tuoi antenati, il tuo totem, ecc.) ritengono importante, come oro, sangue, ossa o simili.\n\nTira+**SAG**. Con un 7+, il GM ti offrirà un indizio utile a risolvere i tuoi problemi attuali o un beneficio per aiutarti. Con un 7-9, l'offerta non basta e dovrai sacrificare anche parte della tua carne. Con un fallimento, ottieni soltanto l'ira degli spiriti capricciosi.",
  roll: {
    stat: 'saggezza',
    on10plus: {
      text: 'Il GM ti offre un indizio utile o un beneficio per aiutarti.',
    },
    on7to9: {
      text: "Stesso effetto del 10+, ma l'offerta non basta e dovrai sacrificare anche parte della tua carne.",
    },
    on6minus: { text: "Ottieni soltanto l'ira degli spiriti capricciosi." },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

export const barbaroAdvanced6to10Moves: readonly Move[] = [
  urloDiBattaglia,
  ilKhanDeiKhan,
  unSegnoDiForza,
  ammazzaliTutti,
  berserker,
  iraPersistente,
  potenzaIndomabile,
  spacca,
  unBuonGiornoPerMorire,
  quelloCheBussa,
  passoDelLampo,
  einherjar,
  essereIlTotem,
  perIlDioDelSangue,
] as const;
